import React, { useCallback, useEffect, useState } from 'react';

import { Autocomplete, ControlGroup, Space } from '@sravni/react-design-system';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';

import { getOrganizationsOptions } from '../../../../helpers/getOrganizationsOptions';
import { getServicesOptions } from '../../../../helpers/reviews';
import { loadAggregatedReviewsCount } from '../../../../services/reviews';
import { useOrganizations } from '../../../../store/hooks/organizations';
import { useRatingSravni, useServicesRating } from '../../../../store/hooks/ratings';
import { IListHeaderProps } from '../../index';
import { HeadingDescription } from '../HeadingDescription';
import { MainSelectButton } from '../MainSelectButton';

export interface IMainSelectProps extends Pick<IListHeaderProps, 'currentServerFilters' | 'reviewTypes'> {
  isMobile: boolean;
  type: PAGE_TYPE;
}

export type TSelectValue = {
  label: string;
  value: string;
  alias: string;
};

const selectInitialValue = {
  label: '',
  value: '',
  alias: '',
};

export const MainSelect: React.FC<IMainSelectProps> = ({ type, reviewTypes, currentServerFilters, isMobile }) => {
  const [organizationValue, setOrganizationValue] = useState<TSelectValue>(selectInitialValue);
  const [serviceValue, setServiceValue] = useState<TSelectValue>(selectInitialValue);
  const [servicesOptions, setServicesOptions] = useState([]);

  const { organizationsList } = useOrganizations();
  const ratingSravni = useRatingSravni();
  const servicesRating = useServicesRating();

  const fetchServiceOptions = useCallback(async () => {
    const { groupedByServices: reviewsCount } = await loadAggregatedReviewsCount({
      organizationType: reviewTypes.reviewService,
      organizationId: organizationValue.value,
    });

    const optionsList = getServicesOptions(currentServerFilters, reviewsCount, type, servicesRating);

    const options =
      serviceValue && !serviceValue.value
        ? optionsList.filter((item) => item.label.toLowerCase().includes(serviceValue.label.toLowerCase()))
        : optionsList;

    setServicesOptions(options);
  }, [currentServerFilters, organizationValue, reviewTypes, serviceValue, servicesRating, type]);

  useEffect(() => {
    if (organizationValue?.value) fetchServiceOptions();
  }, [fetchServiceOptions, organizationValue]);

  const handleSearchOrganization = (value: string) =>
    setOrganizationValue({
      ...selectInitialValue,
      label: value,
    });

  const handleSearchService = (value: string) =>
    setServiceValue({
      ...selectInitialValue,
      label: value,
    });

  const handleChangeOrganization = (value: string) => {
    const selectedRevObj = organizationsList.find((revObj) => revObj.name === value);

    if (selectedRevObj) {
      setOrganizationValue({
        value: selectedRevObj.id,
        label: selectedRevObj.name,
        alias: selectedRevObj.alias,
      });
      return;
    }
  };

  const handleChangeService = (value: string) => {
    const selectedService = servicesOptions?.find((item) => item.label === value);

    if (selectedService) {
      setServiceValue({
        value: selectedService.value,
        label: selectedService.label,
        alias: selectedService.alias,
      });
      return;
    }
  };

  let organizationsOptions = getOrganizationsOptions(organizationsList, ratingSravni);

  if (organizationValue && !organizationValue.value) {
    organizationsOptions = organizationsOptions.filter((item) =>
      item.label.toLowerCase().includes(organizationValue.label.toLowerCase()),
    );
  }

  return (
    <>
      <Space size={12} direction={isMobile ? 'vertical' : 'horizontal'}>
        <ControlGroup direction={isMobile ? 'vertical' : 'horizontal'}>
          <Autocomplete
            options={organizationsOptions}
            label={
              organizationValue.value ? reviewTypes?.namesType.bigFirstChar : `Выберите ${reviewTypes?.namesType.mode5}`
            }
            value={organizationValue.value}
            onChange={handleChangeOrganization}
            onSearch={handleSearchOrganization}
            optionsSort={false}
          />
          <Autocomplete
            options={servicesOptions}
            label={serviceValue.value ? 'Услуга' : 'Выберите услугу'}
            value={serviceValue.value}
            onChange={handleChangeService}
            onSearch={handleSearchService}
            disabled={!organizationValue?.value}
            optionsSort={false}
          />
        </ControlGroup>
        <MainSelectButton
          isMobile={isMobile}
          type={type}
          reviewTypes={reviewTypes}
          organizationValue={organizationValue}
          serviceValue={serviceValue}
        />
      </Space>
      {!isMobile && <HeadingDescription reviewTypes={reviewTypes} />}
    </>
  );
};
